import React, { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { createCheckoutSession } from "../api/billingApi";
import {
  getDealflow,
  getPreferences,
  patchPreferences,
  resetPreferences,
} from "../api/dealflowApi";
import DealCard from "../components/DealCard";
import DisabledFiltersStrip from "../components/DisabledFiltersStrip";
import EmptyState from "../components/EmptyState";
import DebugDrawer from "../components/DebugDrawer";
import ConversionBanner from "../components/ConversionBanner";
import {
  ProUpgradeButton,
  TrialBanner,
} from "../components/billing/PaywallComponents";
import SpotlightTour from "../tour/SpotlightTour";
import { useTour } from "../tour/useTour";

import { Sliders } from "lucide-react";
import { Link } from "react-router-dom";

function VerticalToggle({ vertical, onChange }) {
  const { t } = useTranslation("dashboard");
  return (
    <div className="inline-flex items-center gap-2">
      <div data-testid="vertical-toggle-group" className="inline-flex rounded-xl border border-zinc-800 bg-zinc-950 p-1">
        {["auto", "immo"].map((v) => (
          <button
            key={v}
            data-testid={`vertical-toggle-${v}`}
            onClick={() => onChange(v)}
            className={`px-5 py-2 text-sm rounded-lg transition ${
              vertical === v
                ? "bg-amber-500 text-zinc-950 font-medium"
                : "text-zinc-300 hover:text-zinc-100"
            }`}
          >
            {t(`vertical.${v}`)}
          </button>
        ))}
      </div>
      <Link
        to={`/preferences?vertical=${vertical}`}
        data-testid="prefs-gear-icon"
        aria-label={t("prefs_aria_label")}
        className="rounded-xl border border-zinc-800 bg-zinc-950 p-2 text-zinc-400 hover:text-zinc-100 hover:border-amber-500/40 transition"
      >
        <Sliders className="h-4 w-4" />
      </Link>
    </div>
  );
}

// Final DealFlow Product Logic (2026-07) — session-only quality mode.
// Default is ALWAYS "good_only" (strict deals ≥10 % under market). The
// end-of-feed CTA switches the mode temporarily for this session; a
// fresh visit / login / filter-reset returns to good_only.
const QUALITY_MODE_DEFAULT = "good_only";

function QualityModeBanner({ mode, onBack, t }) {
  if (mode === QUALITY_MODE_DEFAULT) return null;
  return (
    <div
      data-testid="quality-mode-banner"
      className="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-zinc-400">{t("quality_mode.active_prefix")}:</span>
        <span
          data-testid="quality-mode-active-pill"
          className="px-2.5 py-0.5 rounded-full bg-amber-500 text-zinc-950 text-xs font-semibold uppercase tracking-wide"
        >
          {t(`quality_mode.label_${mode}`)}
        </span>
        <span className="text-zinc-500 text-xs">{t("quality_mode.temp_note")}</span>
      </div>
      <button
        data-testid="back-to-deals-btn"
        onClick={onBack}
        className="px-3 py-1.5 rounded-lg bg-emerald-500 text-zinc-950 text-sm font-medium hover:bg-emerald-400 transition"
      >
        {t("quality_mode.back_to_deals")}
      </button>
    </div>
  );
}

function QualityModeCta({ mode, onSwitch, t }) {
  const options = [
    { m: "market_fair", label: t("quality_mode.cta_market_fair"), testid: "show-market-fair-btn" },
    { m: "overpriced_only", label: t("quality_mode.cta_overpriced"), testid: "show-overpriced-btn" },
    { m: "all", label: t("quality_mode.cta_all_rated"), testid: "show-all-rated-btn" },
  ].filter((o) => o.m !== mode);
  return (
    <div
      data-testid="end-of-feed-cta"
      className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/50 px-6 py-7 text-center space-y-4"
    >
      <div className="text-zinc-100 font-medium">
        {t("quality_mode.end_of_feed_heading")}
      </div>
      <p className="text-sm text-zinc-400">{t("quality_mode.end_of_feed_sub")}</p>
      <div className="flex flex-wrap justify-center gap-3">
        {options.map((o) => (
          <button
            key={o.m}
            data-testid={o.testid}
            onClick={() => onSwitch(o.m)}
            className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-200 text-sm hover:border-amber-500/60 hover:text-amber-300 transition"
          >
            {o.label}
          </button>
        ))}
        {mode !== QUALITY_MODE_DEFAULT && (
          <button
            data-testid="end-of-feed-back-to-deals-btn"
            onClick={() => onSwitch(QUALITY_MODE_DEFAULT)}
            className="px-4 py-2 rounded-lg bg-emerald-500 text-zinc-950 text-sm font-medium hover:bg-emerald-400 transition"
          >
            {t("quality_mode.back_to_deals")}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation("dashboard");
  const { t: tc } = useTranslation("common");
  const deckTour = useTour("tour_deck_done");
  const qc = useQueryClient();
  const { user, loading: authLoading, isPro, accessReason, trialUntil, refreshUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();

  // Phase E — handle Stripe-return query params on mount.
  // `?stripe=success` → green toast + refetch /me so isPro flips.
  // `?upgrade=auto`   → bounce straight into a fresh checkout session
  //                     (used by the landing-page CTA for logged-in users).
  const [successToast, setSuccessToast] = useState(false);
  useEffect(() => {
    const stripeReturn = searchParams.get("stripe");
    const upgrade = searchParams.get("upgrade");

    if (stripeReturn === "success") {
      setSuccessToast(true);
      refreshUser();
      // Strip the query params so reload doesn't re-toast.
      const next = new URLSearchParams(searchParams);
      next.delete("stripe");
      next.delete("session_id");
      setSearchParams(next, { replace: true });
      const t = setTimeout(() => setSuccessToast(false), 6000);
      return () => clearTimeout(t);
    }
    if (upgrade === "auto" && !isPro) {
      // Strip first so a network error doesn't trigger an infinite
      // checkout loop on reload.
      const next = new URLSearchParams(searchParams);
      next.delete("upgrade");
      setSearchParams(next, { replace: true });
      createCheckoutSession()
        .then(({ checkout_url }) => {
          if (checkout_url) window.location.href = checkout_url;
        })
        .catch(() => {
          /* swallow — user can still click the upgrade button */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: prefs } = useQuery({
    queryKey: ["preferences"],
    queryFn: getPreferences,
    enabled: !authLoading && !!user,
  });

  // Default vertical: `?vertical=` URL param wins (used by
  // PreferencesPageV2 save-redirect so the user lands on the vertical
  // they just edited), else prefs.default_vertical, else "auto".
  const verticalParam = searchParams.get("vertical");
  const paramVertical =
    verticalParam === "immo" || verticalParam === "auto" ? verticalParam : null;
  const initialVertical =
    paramVertical || (prefs?.default_vertical === "real_estate" ? "immo" : "auto");
  const [vertical, setVertical] = useState(initialVertical);
  React.useEffect(() => {
    // URL param takes precedence over the persisted default so a
    // save-redirect from /preferences?vertical=immo lands on IMMO.
    if (paramVertical) {
      setVertical(paramVertical);
      return;
    }
    // Fallback: sync to prefs.default_vertical once prefs load.
    if (prefs && prefs.default_vertical) {
      const v = prefs.default_vertical === "real_estate" ? "immo" : "auto";
      setVertical(v);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramVertical, prefs?.default_vertical]);

  const [includeUnchecked, setIncludeUnchecked] = useState(true);
  const [includeDead, setIncludeDead] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [qualityMode, setQualityMode] = useState(QUALITY_MODE_DEFAULT);

  const country = prefs?.country || null;

  const queryKey = useMemo(
    () => ["dealflow", vertical, country || "", includeUnchecked, includeDead, qualityMode],
    [vertical, country, includeUnchecked, includeDead, qualityMode],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      getDealflow({
        vertical,
        cursor: pageParam,
        limit: 24,
        include_unchecked: includeUnchecked,
        include_dead: includeDead,
        quality_mode: qualityMode,
      }),
    getNextPageParam: (last) => last.next_cursor || undefined,
    initialPageParam: null,
    enabled: !authLoading && !!user,
  });

  const persistVertical = useMutation({
    mutationFn: (v) =>
      patchPreferences({ default_vertical: v === "immo" ? "real_estate" : "auto" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["preferences"] }),
  });

  const onVerticalChange = (v) => {
    setVertical(v);
    // Phase H2-followup Item 4 — also stash the last-used vertical so
    // PreferencesPage can hydrate default_vertical without surfacing a UI.
    try {
      window.localStorage?.setItem("dealalert_last_vertical", v);
    } catch {
      /* sessionStorage disabled — ignore */
    }
    persistVertical.mutate(v);
  };

  const onReset = async () => {
    await resetPreferences(vertical);
    setQualityMode(QUALITY_MODE_DEFAULT);
    await qc.invalidateQueries({ queryKey: ["preferences"] });
    await refetch();
  };

  // Final DealFlow Product Logic (2026-07) — deliberate, session-only
  // mode switch triggered by the end-of-feed CTA. Never persisted.
  const onSwitchQualityMode = (mode) => {
    setQualityMode(mode);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const allItems = data?.pages.flatMap((p) => p.items) || [];
  const lastPageMeta = data?.pages[data.pages.length - 1]?.meta;
  const disabledFilters = lastPageMeta?.disabled_filters || [];
  const reasonIfEmpty = lastPageMeta?.reason_if_empty || null;
  // Phase E — when the backend caps the feed, it flips `paywall: true`
  // on the response and includes `paywall_total_blocked`. Render the
  // upgrade card after the 5 teasers.
  const dealflowPaywalled = data?.pages?.some((p) => p.paywall === true);
  const totalBlocked =
    data?.pages?.[data.pages.length - 1]?.paywall_total_blocked || 0;

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {successToast && (
        <div
          data-testid="billing-success-toast"
          className="rounded-2xl border border-emerald-500/40 bg-emerald-500/15 px-4 py-3 text-sm text-emerald-100 flex items-center gap-2"
        >
          <span aria-hidden className="text-emerald-300">✓</span>
          {t("welcome_pro")} {t("access_unlocked")}
        </div>
      )}

      {accessReason === "trial" && (
        <TrialBanner trialUntil={trialUntil} />
      )}

      {/* Phase H2-followup round-7 — Item 4: Auto/Immo toggle + gear
          icon promoted to the very top of the dashboard so the filter
          control is visible in the first viewport. Previously these
          lived inside the DealFlow header row, which Top Deals pushed
          below the fold on mobile-browser + small-laptop viewports. */}
      <div
        data-testid="dashboard-top-controls"
        className="flex items-center justify-end"
      >
        <VerticalToggle vertical={vertical} onChange={onVerticalChange} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="dealflow-controls-row">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl text-zinc-100 font-medium tracking-tight">
            {t("heading")}
          </h1>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {country && (
            <span className="text-zinc-400">
              {t("country_label")}:{" "}
              <span className="text-zinc-200 font-medium">{country}</span>
            </span>
          )}
          <button
            data-testid="reset-filters-btn"
            onClick={onReset}
            className="px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-200 hover:border-amber-500/60 hover:text-amber-300"
          >
            {t("reset_filters")}
          </button>
          {isAdmin && (
            <button
              data-testid="open-debug-btn"
              onClick={() => setDebugOpen(true)}
              className="px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            >
              {t("debug_button")} ◢
            </button>
          )}
        </div>
      </div>

      {/* Sprint M Item 4 — real-data conversion banner. Reads live counts
          from /api/insights/daily-summary. Renders nothing if no bargains
          today (no-op-defensive). Different copy for Pro vs Free. */}
      <ConversionBanner
        vertical={vertical === "immo" ? "real_estate" : vertical}
        country={country}
      />

      <div className="flex items-center gap-4 text-sm text-zinc-400">
        {isAdmin && (
          <>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="toggle-include-unchecked"
                checked={includeUnchecked}
                onChange={(e) => setIncludeUnchecked(e.target.checked)}
                className="accent-amber-500"
              />
              {t("toggle_unchecked")}
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="toggle-include-dead"
                checked={includeDead}
                onChange={(e) => setIncludeDead(e.target.checked)}
                className="accent-amber-500"
              />
              {t("toggle_dead")}
            </label>
          </>
        )}
      </div>

      <DisabledFiltersStrip disabled={disabledFilters} />

      <QualityModeBanner
        mode={qualityMode}
        onBack={() => onSwitchQualityMode(QUALITY_MODE_DEFAULT)}
        t={t}
      />

      {isLoading ? (
        <div className="text-zinc-500">{t("ui.loading", { ns: "common", defaultValue: "Loading…" })}</div>
      ) : isError ? (
        <div className="text-rose-400">{t("error_label")}: {String(error.message || error)}</div>
      ) : allItems.length === 0 ? (
        <>
          <EmptyState
            reason={reasonIfEmpty}
            country={country}
            onReset={onReset}
            onRetry={() => refetch()}
            // Mobile-Web-Parity Cycle: the "Show unverified images" CTA
            // is paired with the admin-only `toggle-include-unchecked`
            // control. Only pass the callback for admins so the EmptyState
            // renders the toggle CTA exclusively when the user can
            // actually act on it.
            onToggleUnchecked={isAdmin ? () => setIncludeUnchecked((v) => !v) : undefined}
          />
          {/* Kein automatisches Nachladen anderer Qualitätsstufen —
              der Wechsel ist immer eine bewusste Nutzeraktion. */}
          <QualityModeCta mode={qualityMode} onSwitch={onSwitchQualityMode} t={t} />
        </>
      ) : (
        <>
          <div
            data-testid="dealcard-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {allItems.map((c) => (
              <DealCard key={c.id} card={c} />
            ))}
          </div>
          {dealflowPaywalled && (
            <div
              data-testid="dealflow-paywall-card"
              className="mt-6 rounded-2xl border border-emerald-500/30 bg-zinc-900/60 px-6 py-7 text-center space-y-3"
            >
              <div className="text-emerald-300 text-xs tracking-wide uppercase">
                DealAlert Pro
              </div>
              <div className="text-zinc-100 text-lg font-medium">
                {t("paywall_heading")}
              </div>
              {totalBlocked > 0 && (
                <div className="text-zinc-400 text-sm">
                  {t("paywall_blocked_text", { count: totalBlocked })}
                </div>
              )}
              <div className="pt-1">
                <ProUpgradeButton data-testid="dealflow-paywall-upgrade-button">
                  {t("paywall_cta")}
                </ProUpgradeButton>
              </div>
            </div>
          )}
          {hasNextPage && (
            <div className="text-center pt-4">
              <button
                data-testid="load-more-btn"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="px-5 py-2 rounded-lg border border-zinc-700 text-zinc-200 hover:border-amber-500/60 hover:text-amber-300 disabled:opacity-50"
              >
                {isFetchingNextPage ? t("ui.loading", { ns: "common", defaultValue: "Loading…" }) : t("load_more")}
              </button>
            </div>
          )}
          {/* Final DealFlow Product Logic (2026-07) — end-of-feed CTA:
              only after the LAST page and never behind the paywall. */}
          {!hasNextPage && !dealflowPaywalled && (
            <QualityModeCta mode={qualityMode} onSwitch={onSwitchQualityMode} t={t} />
          )}
        </>
      )}

      {/* TourGuide — deck tour on the real deal grid (once per user) */}
      {deckTour.eligible && !isLoading && !isError && allItems.length > 0 && (
        <SpotlightTour
          idPrefix="tour-deck"
          onFinish={deckTour.finish}
          steps={[
            {
              target: '[data-testid="vertical-toggle-group"]',
              title: tc("tour.deck.step1_title"),
              body: tc("tour.deck.step1_body"),
            },
            {
              target: '[data-testid="dealcard-grid"] [data-testid^="deal-card-"]',
              title: tc("tour.deck.step2_title"),
              body: tc("tour.deck.step2_body"),
            },
            {
              target: '[data-testid="prefs-gear-icon"]',
              title: tc("tour.deck.step3_title"),
              body: tc("tour.deck.step3_body"),
            },
          ]}
        />
      )}

      {debugOpen && (
        <DebugDrawer
          vertical={vertical}
          country={country}
          onClose={() => setDebugOpen(false)}
        />
      )}

      {/* P0 2026-07-29 — expired-paywall lock moved to the central
          ProtectedRoute gate (no permanent free tier). */}
    </div>
  );
}
